'use client';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarIcon } from '@radix-ui/react-icons';
import { DateRange } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button } from './Button';
import {
  cn,
  dateTimeFormatWithoutSeconds,
  formatDateTime,
  utcDateRangeToZoned,
  zonedDateRangeToUtc,
} from '@ais-ui/utils';
import { Calendar } from './Calendar';
import { Input } from './Input';
import { Label } from './Label';
import { Separator } from './Separator';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { clampTimeValue } from '@ais-ui/utils';
import { applyDateTimeDefaults } from './applyDateTimeDefaults';

// Define the props for the component
interface DatePickerWithRangeProps {
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  timeZone: string;
  dateTimeFormat: string;
  dateLimit: 'past' | 'future' | 'all';
  disabled?: boolean;
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  className?: string;
  showPresets?: boolean;
  onPresetSelect?: (from: Date, to: Date) => void;
  /** Override default time for the FROM date when a new date is selected */
  defaultFromTime?: { hours: number; minutes: number };
  /** Override default time for the TO date when a new date is selected. Use 'now' for current time. */
  defaultToTime?: 'now' | { hours: number; minutes: number };
}

const DATE_PRESETS: {
  label: string;
  getRange: () => { from: Date; to: Date };
}[] = [
  {
    label: 'Last 5 min',
    getRange: () => {
      const now = new Date();
      return { from: new Date(now.getTime() - 5 * 60 * 1000), to: now };
    },
  },
  {
    label: 'Last 15 min',
    getRange: () => {
      const now = new Date();
      return { from: new Date(now.getTime() - 15 * 60 * 1000), to: now };
    },
  },
  {
    label: 'Last 1 hour',
    getRange: () => {
      const now = new Date();
      return { from: new Date(now.getTime() - 60 * 60 * 1000), to: now };
    },
  },
  {
    label: 'Last 6 hours',
    getRange: () => {
      const now = new Date();
      return { from: new Date(now.getTime() - 360 * 60 * 1000), to: now };
    },
  },
  {
    label: 'Last 24 hours',
    getRange: () => {
      const now = new Date();
      return { from: new Date(now.getTime() - 1440 * 60 * 1000), to: now };
    },
  },
  {
    label: 'Today',
    getRange: () => {
      const now = new Date();
      const from = new Date(now);
      from.setHours(0, 0, 0, 0);
      return { from, to: now };
    },
  },
  {
    label: 'Yesterday',
    getRange: () => {
      const now = new Date();
      const from = new Date(now);
      from.setDate(from.getDate() - 1);
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    },
  },
];

export const DateTimePickerWithRange = ({
  className,
  date,
  setDate,
  timeZone = 'UTC',
  dateTimeFormat,
  dateLimit = 'all',
  disabled = false,
  startDatetimeThreshold,
  endDatetimeThreshold,
  showPresets = false,
  onPresetSelect,
  defaultFromTime,
  defaultToTime,
}: DatePickerWithRangeProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const localDate = useMemo(
    () => utcDateRangeToZoned(date, timeZone),
    [date, timeZone],
  );

  const [defaultMonth, setDefaultMonth] = useState<Date | undefined>(
    localDate?.from,
  );

  useEffect(() => {
    if (open && localDate?.from) {
      setDefaultMonth(localDate.from);
    }
  }, [open, localDate]);

  const setUtcDateRange = useCallback(
    (dateRange: DateRange | undefined) => {
      setDate(zonedDateRangeToUtc(dateRange, timeZone));
    },
    [setDate, timeZone],
  );

  const handleDateSelection = useCallback(
    (selectedDate: DateRange | undefined) => {
      applyDateTimeDefaults(selectedDate, {
        currentDate: date,
        timeZone,
        dateLimit,
        defaultFromTime,
        defaultToTime,
      });

      setUtcDateRange(selectedDate);
    },
    [
      date,
      timeZone,
      setUtcDateRange,
      dateLimit,
      defaultFromTime,
      defaultToTime,
    ],
  );

  const onSelect = () => {
    setOpen(false);
  };

  const disableDate = useCallback(
    (zonedDateToCheck: Date) => {
      const today = new Date();
      const zonedToday = toZonedTime(today, timeZone);
      zonedToday.setHours(0, 0, 0, 0);

      if (startDatetimeThreshold && zonedDateToCheck < startDatetimeThreshold) {
        return true;
      }
      if (endDatetimeThreshold && zonedDateToCheck > endDatetimeThreshold) {
        return true;
      }

      switch (dateLimit) {
        case 'past':
          return zonedDateToCheck > zonedToday;
        case 'future':
          return zonedDateToCheck < zonedToday;
        case 'all':
        default:
          return false;
      }
    },
    [timeZone, startDatetimeThreshold, endDatetimeThreshold, dateLimit],
  );

  const formattedDate = useMemo(() => {
    if (!localDate) {
      return 'Pick a date and time';
    }

    if (localDate.from && localDate.to) {
      const formattedFromDate = formatDateTime(
        localDate.from,
        dateTimeFormatWithoutSeconds(dateTimeFormat),
      );
      const formattedToDate = formatDateTime(
        localDate.to,
        dateTimeFormatWithoutSeconds(dateTimeFormat),
      );
      return `${formattedFromDate} - ${formattedToDate}`;
    } else if (localDate.from) {
      const formattedFromDate = formatDateTime(
        localDate.from,
        dateTimeFormatWithoutSeconds(dateTimeFormat),
      );
      return formattedFromDate;
    } else {
      return 'Pick a date and time';
    }
  }, [localDate, dateTimeFormat]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const fromYear = useMemo(() => currentYear - 5, [currentYear]);
  const toYear = useMemo(() => currentYear + 2, [currentYear]);

  const onChangeFrom = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: 'minutes' | 'hours') => {
      const value = e.target.value.trim();
      if (!value) {
        // If no value is provided, set the whole date range to undefined
        setDate(undefined);
      } else if (!localDate || !localDate.from) {
        setDate(undefined);
      } else {
        const numberValue = clampTimeValue(parseInt(value, 10), type);

        const updatedLocalDate = new Date(localDate.from);

        if (type === 'minutes') {
          updatedLocalDate.setMinutes(numberValue);
        } else if (type === 'hours') {
          updatedLocalDate.setHours(numberValue);
        }

        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }

          const updatedUtcDate = fromZonedTime(updatedLocalDate, timeZone);

          // Makes sure to update to, if it's before from
          if (prevDate.to && prevDate.to < updatedUtcDate) {
            return {
              from: updatedUtcDate,
              to: updatedUtcDate,
            };
          }

          return {
            ...prevDate,
            from: fromZonedTime(updatedLocalDate, timeZone),
          };
        });
      }
    },
    [localDate, setDate, timeZone],
  );

  const onChangeTo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: 'minutes' | 'hours') => {
      const value = e.target.value.trim();
      if (!value) {
        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }
          return {
            ...prevDate,
            to: undefined,
          };
        });
      } else if (!localDate || !localDate.to) {
        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }
          return {
            ...prevDate,
            to: undefined,
          };
        });
      } else {
        const numberValue = clampTimeValue(parseInt(value, 10), type);

        const updatedLocalDate = new Date(localDate.to);

        if (type === 'minutes') {
          updatedLocalDate.setMinutes(numberValue);
        } else if (type === 'hours') {
          updatedLocalDate.setHours(numberValue);
        }

        setDate((prevDate) => {
          if (!prevDate) {
            return undefined;
          }

          const updatedUtcDate = fromZonedTime(updatedLocalDate, timeZone);

          // Makes sure to update from, if it's after to
          if (prevDate.from && prevDate.from > updatedUtcDate) {
            return {
              from: updatedUtcDate,
              to: updatedUtcDate,
            };
          }

          return {
            ...prevDate,
            to: updatedUtcDate,
          };
        });
      }
    },
    [localDate, setDate, timeZone],
  );

  return (
    <div className={cn('grid gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            disabled={disabled}
            className={cn(
              'w-full justify-start text-left font-normal',
              !localDate && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="mr-2 size-4 shrink-0" />
            <span className="truncate">{formattedDate}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 flex flex-col" align="start">
          {showPresets && (
            <>
              <div
                className="flex flex-wrap gap-1 p-3"
                style={{ width: 0, minWidth: '100%' }}
              >
                {DATE_PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      const { from, to } = preset.getRange();
                      if (onPresetSelect) {
                        onPresetSelect(from, to);
                      } else {
                        setDate({ from, to });
                      }
                      setOpen(false);
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              <Separator />
            </>
          )}
          <Calendar
            initialFocus
            mode="range"
            captionLayout="dropdown"
            defaultMonth={defaultMonth}
            selected={localDate}
            onSelect={handleDateSelection}
            numberOfMonths={2}
            fromYear={fromYear}
            toYear={toYear}
            disabled={disableDate}
          />
          <Separator></Separator>

          <div className="grid grid-cols-11">
            <div className="col-start-1 col-span-5 p-4">
              <div className="flex justify-between items-center">
                <Input
                  value={
                    localDate?.from
                      ? String(localDate.from.getHours()).padStart(2, '0')
                      : ''
                  }
                  id="from-hours"
                  placeholder="HH"
                  type="number"
                  onChange={(e) => onChangeFrom(e, 'hours')}
                  className="h-8 w-[70px]"
                ></Input>
                <Label> : </Label>
                <Input
                  value={
                    localDate?.from
                      ? String(localDate.from.getMinutes()).padStart(2, '0')
                      : ''
                  }
                  id="from-minutes"
                  placeholder="MM"
                  type="number"
                  onChange={(e) => onChangeFrom(e, 'minutes')}
                  className="h-8 w-[70px]"
                ></Input>
              </div>
            </div>
            <div className="flex col-span-1 justify-center">
              <Separator
                className="h-full col-span-1"
                orientation="vertical"
              ></Separator>
            </div>

            <div className="space-x-4 col-start-7 col-span-5 p-4">
              <div className="flex justify-between items-center">
                <Input
                  value={
                    localDate?.to
                      ? String(localDate.to.getHours()).padStart(2, '0')
                      : ''
                  }
                  id="to-hours"
                  placeholder="HH"
                  type="number"
                  onChange={(e) => onChangeTo(e, 'hours')}
                  className="h-8 w-[70px]"
                ></Input>

                <Label> : </Label>

                <Input
                  value={
                    localDate?.to
                      ? String(localDate.to.getMinutes()).padStart(2, '0')
                      : ''
                  }
                  id="to-minutes"
                  placeholder="MM"
                  type="number"
                  onChange={(e) => onChangeTo(e, 'minutes')}
                  className="h-8 w-[70px]"
                ></Input>
              </div>
            </div>
          </div>
          <Separator></Separator>
          <div className="flex p-4 justify-between items-baseline w-full">
            <div className="flex space-x-2 w-full grow">
              <Label className="bold text-primary">Selected:</Label>
              <Label className="text-muted-foreground">{formattedDate}</Label>
            </div>
            <Button
              id="datetimerange-select"
              className=""
              onClick={onSelect}
              type="submit"
            >
              Select
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
